import type { AnnotationUnderUserReviewT, SAMAnnotationMetadata } from '../../../../common/types/types';
import type { SamModelImagePluginRequestT, SamModelVideoPluginRequestT } from '../../../types';
import { ImageSegmentationType, VideoSegmentationType } from '../../../types';
import { SAM_ANNOTATION_ACTIONS } from '../../constants';
import type { BoundingBoxT } from '../../types';
import { DEFAULT_FRAME_RATE } from '../constants';
const convertBoundingBoxToPayload = ({
  data: boundingBox
}: {
  data: BoundingBoxT;
}) => ({
  xStart: Math.floor(boundingBox.x),
  yStart: Math.floor(boundingBox.y),
  xEnd: Math.floor(boundingBox.x + boundingBox.width),
  yEnd: Math.floor(boundingBox.y + boundingBox.height)
});
const convertPointsToPayload = ({
  data: boundingBox,
  type
}: {
  data: BoundingBoxT;
  type: 'INCLUDE' | 'EXCLUDE';
}) => ({
  x: Math.floor(boundingBox.x),
  y: Math.floor(boundingBox.y),
  includeInMask: type === 'INCLUDE'
});
const getFirstSAMAnnotationMetadata = (annotationMetaData: SAMAnnotationMetadata[]): SAMAnnotationMetadata => {
  const firstAnnotationMetaData = annotationMetaData[0];
  if (!firstAnnotationMetaData) {
    throw new Error('SAM annotation metadata is required');
  }
  return firstAnnotationMetaData;
};
export const createSAMSegmentImagePayload = (annotation: AnnotationUnderUserReviewT, imageUrl: string): SamModelImagePluginRequestT => {
  switch (annotation.type) {
    case SAM_ANNOTATION_ACTIONS.BOUNDING_BOX:
      {
        return {
          imageUrl,
          imageSegmentationType: ImageSegmentationType.IMAGE_SEGMENTATION_TYPE_MASK,
          box: convertBoundingBoxToPayload(getFirstSAMAnnotationMetadata(annotation.annotationMetaData))
        };
      }
    case SAM_ANNOTATION_ACTIONS.POINT:
      {
        return {
          imageUrl,
          imageSegmentationType: ImageSegmentationType.IMAGE_SEGMENTATION_TYPE_MASK,
          points: annotation.annotationMetaData.map(metaData => convertPointsToPayload(metaData))
        };
      }
    case SAM_ANNOTATION_ACTIONS.POLYGON:
      {
        return {
          imageUrl,
          imageSegmentationType: ImageSegmentationType.IMAGE_SEGMENTATION_TYPE_POLYGON,
          points: annotation.annotationMetaData.map(metaData => convertPointsToPayload(metaData))
        };
      }
    case SAM_ANNOTATION_ACTIONS.BOUNDING_BOX_POLYGON:
      {
        return {
          imageUrl,
          imageSegmentationType: ImageSegmentationType.IMAGE_SEGMENTATION_TYPE_POLYGON,
          box: convertBoundingBoxToPayload(getFirstSAMAnnotationMetadata(annotation.annotationMetaData))
        };
      }
    default:
      {
        return {
          imageUrl,
          imageSegmentationType: ImageSegmentationType.IMAGE_SEGMENTATION_TYPE_INVALID,
          points: []
        };
      }
  }
};
export const createSAMSegmentVideoPayload = (annotation: AnnotationUnderUserReviewT, videoUrl: string, frameRange: [number, number], _frameRate: number = DEFAULT_FRAME_RATE): SamModelVideoPluginRequestT => {
  switch (annotation.type) {
    case SAM_ANNOTATION_ACTIONS.BOUNDING_BOX:
      {
        return {
          videoUrl,
          videoSegmentationType: VideoSegmentationType.VIDEO_SEGMENTATION_TYPE_MASK,
          box: convertBoundingBoxToPayload(getFirstSAMAnnotationMetadata(annotation.annotationMetaData)),
          frameIndex: annotation?.frameIndex ?? 0,
          frameRange: {
            startFrame: frameRange[0],
            endFrame: frameRange[1]
          }
        };
      }
    case SAM_ANNOTATION_ACTIONS.POINT:
      {
        return {
          videoUrl,
          videoSegmentationType: VideoSegmentationType.VIDEO_SEGMENTATION_TYPE_MASK,
          points: annotation.annotationMetaData.map(metaData => convertPointsToPayload(metaData)),
          frameIndex: annotation?.frameIndex ?? 0,
          frameRange: {
            startFrame: frameRange[0],
            endFrame: frameRange[1]
          }
        };
      }
    case SAM_ANNOTATION_ACTIONS.POLYGON:
      {
        return {
          videoUrl,
          videoSegmentationType: VideoSegmentationType.VIDEO_SEGMENTATION_TYPE_POLYGON,
          points: annotation.annotationMetaData.map(metaData => convertPointsToPayload(metaData)),
          frameIndex: annotation?.frameIndex ?? 0,
          frameRange: {
            startFrame: frameRange[0],
            endFrame: frameRange[1]
          }
        };
      }
    default:
      {
        return {
          videoUrl,
          videoSegmentationType: VideoSegmentationType.VIDEO_SEGMENTATION_TYPE_INVALID,
          frameIndex: 0,
          frameRange: {
            startFrame: 0,
            endFrame: 0
          }
        };
      }
  }
};
export const createAnnotationUnderReviewData = ({
  annotation,
  color
}: {
  annotation: AnnotationUnderUserReviewT;
  color: string;
}, points?: [number, number][], mask?: string, automationRequestId?: string): AnnotationUnderUserReviewT => {
  switch (annotation.type) {
    case SAM_ANNOTATION_ACTIONS.BOUNDING_BOX:
    case SAM_ANNOTATION_ACTIONS.POINT:
      {
        return {
          ...annotation,
          annotationData: [{
            base64String: mask,
            color
          }],
          automationRequestId
        };
      }
    case SAM_ANNOTATION_ACTIONS.POLYGON:
    case SAM_ANNOTATION_ACTIONS.BOUNDING_BOX_POLYGON:
      {
        return {
          ...annotation,
          annotationData: [{
            points: points ?? []
          }],
          automationRequestId
        };
      }
    case SAM_ANNOTATION_ACTIONS.AUTO_DETECT:
      {
        return {
          ...annotation,
          annotationData: [{
            base64String: mask,
            color: annotation.annotationMetaData[0] ? ((annotation.annotationMetaData[0] as unknown) as {
              color?: string;
            }).color ?? '' : ''
          }],
          automationRequestId
        };
      }
    case SAM_ANNOTATION_ACTIONS.AUTO_DETECT_POLYGON:
      {
        return {
          ...annotation,
          annotationData: [{
            points: points ?? []
          }],
          automationRequestId
        };
      }
  }
};